import i18n from '@/utils/i18n';
import InvoiceIcon from '@assets/icons/invoice.svg';
import MenuIcon from '@assets/icons/menu.svg';
import PrinterIcon from '@assets/icons/printer.svg';
import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { Analytics } from '@/utils/analytics';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import {
  CreditRequest_RenderPrintTemplateQuery,
  CreditRequest_RenderPrintTemplateQueryVariables,
  Invoice_GetPdfUrlQuery,
  Invoice_GetPdfUrlQueryVariables,
} from '@/types';
import { CreditRequestDetailList } from '@/components/credit-request/credit-request-detail-list';
import { CreditRequestDetailPrices } from './credit-request-detail-prices';
import { CreditRequestPlacedBy } from '@/components/credit-request/credit-request-placed-by';
import {
  FONTS,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  ThemedSVGProps,
} from '@/styles';
import { formatCurrency } from '@/utils/money';
import { gql } from '@apollo/client';
import { isPepperInvoicePDFHidden } from '@/utils/invoice';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { MultiStyleText } from '@/components/multi-style-text';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { PageContainer } from '@/components/page-container';
import { PrintLabelConfirmation } from '@/components/print-label-confirmation';
import { RENDER_PRINT_CREDIT_REQUEST_TEMPLATE_QUERY } from '@/hooks/credit-requests/credit-request-queries';
import { SvgProps } from 'react-native-svg';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useGetCreditRequestBatch } from '@/components/credit-request/hooks/use-get-credit-request-batch';
import { useGetOpenInvoicePDF } from '@/hooks/use-get-open-invoice-pdf';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_INVOICE_PDF_URL = gql`
  query Invoice_GetPdfUrl($invoiceUUID: uuid!) {
    getOrderInvoiceById(order_invoice_id: $invoiceUUID) {
      order_invoice {
        document_url
        seen
        uuid
      }
      public_url
    }
  }
`;

interface Props {
  creditRequestBatchUUID: string;
  navigationHeaderContainerStyle?: StyleProp<ViewStyle>;
}

type CreditRequestActionListOption = ListOption<{
  Icon: React.ComponentType<SvgProps>;
  iconProps?: ThemedSVGProps;
}>;

enum CreditRequestActionSheetAction {
  SendToMobilePrinter = 'SEND_TO_MOBILE_PRINTER',
  ViewInvoicePDF = 'VIEW_INVOICE_PDF',
}

enum CreditRequestActionSheetRoute {
  Actions = 'ACTIONS',
  PrintConfirmation = 'PRINT_CONFIRMATION',
}

export const CreditRequestDetailComponent: React.FC<Props> = ({
  creditRequestBatchUUID,
  navigationHeaderContainerStyle,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { isSmallLayout } = useLayoutContext();
  const { loading, data } = useGetCreditRequestBatch(creditRequestBatchUUID);
  const { currencyCode } = usePaymentsContext();
  const [styles, theme] = useStyles();
  const { showToast } = useToasts();
  const printCreditRequestEnabled = useDynamicConfigValue(
    'credit_request_config',
  )?.print_credit_request_enabled;

  const [showMoreActionsSheet, setShowMoreActionsSheet] = useState(false);
  const creditRequestBatch = data?.credit_request_batches_by_pk;
  const creditRequestBatchLineItems =
    creditRequestBatch?.credit_request_batch_line_items;

  const { data: originalInvoiceData } = useWrappedQuery<
    Invoice_GetPdfUrlQuery,
    Invoice_GetPdfUrlQueryVariables
  >(GET_INVOICE_PDF_URL, {
    skip: !creditRequestBatch?.order_invoice_uuid,
    variables: {
      invoiceUUID: creditRequestBatch?.order_invoice_uuid!,
    },
  });

  const hasInvoiceDetailsData = Boolean(
    originalInvoiceData?.getOrderInvoiceById,
  );

  const disablePepperInvoicePdf = useSupplierDynamicConfigValue(
    'disable_pepper_invoice_pdf',
    false,
  );
  const showInvoicePDF = !isPepperInvoicePDFHidden({
    disablePepperInvoicePdf: Boolean(disablePepperInvoicePdf),
    hasCustomPdf: Boolean(
      originalInvoiceData?.getOrderInvoiceById.order_invoice?.document_url,
    ),
  });

  const openInvoicePDF = useGetOpenInvoicePDF(
    originalInvoiceData?.getOrderInvoiceById.public_url,
    originalInvoiceData?.getOrderInvoiceById.order_invoice?.uuid,
    originalInvoiceData?.getOrderInvoiceById.order_invoice?.seen,
  );

  const renderCreditRequestTemplate = useLazyQuery<
    CreditRequest_RenderPrintTemplateQuery,
    CreditRequest_RenderPrintTemplateQueryVariables
  >(RENDER_PRINT_CREDIT_REQUEST_TEMPLATE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const creditRequestActions = useMemo(() => {
    const actions = [];

    if (hasInvoiceDetailsData && showInvoicePDF) {
      actions.push({
        metadata: {
          Icon: InvoiceIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
        },
        title: i18n.t('view_invoice_pdf'),
        value: CreditRequestActionSheetAction.ViewInvoicePDF,
      });
    }

    if (printCreditRequestEnabled && Platform.OS !== 'web') {
      actions.push({
        metadata: {
          Icon: PrinterIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
        },
        title: i18n.t('send_to_mobile_printer'),
        value: CreditRequestActionSheetAction.SendToMobilePrinter,
      });
    }

    return actions;
  }, [hasInvoiceDetailsData, printCreditRequestEnabled, showInvoicePDF, theme]);

  const navigationHeaderProps = useMemo((): NavigationHeaderContentProps => {
    if (loading || !creditRequestBatch) {
      return {
        showBackButton: isSmallLayout,
        showShadow: false,
        title: (
          <View style={styles.loadingContainer}>
            <ActivityIndicator />
          </View>
        ),
      };
    }

    const unitCount = creditRequestBatchLineItems?.reduce(
      (result, cri) => result + (cri.quantity ?? 0),
      0,
    );

    const totalPrice = creditRequestBatchLineItems
      ? formatCurrency(
          creditRequestBatchLineItems?.reduce(
            (result, cri) => result + cri.pack_price_in_micros * cri.quantity,
            0,
          ),
          currencyCode,
        )
      : '';

    const hasActions = creditRequestActions.length > 0;

    const actions = hasActions
      ? [
          {
            Icon: MenuIcon,
            iconProps: {
              testID: 'credit-request-detail-menu-button',
            },
            onPress: () => {
              Analytics.track('credit_request_detail.menu_button.press');
              setShowMoreActionsSheet(true);
            },
          },
        ]
      : [];

    if (isSmallLayout) {
      return {
        actions,
        centerTitle: isSmallLayout,
        contentFooter: creditRequestBatch ? (
          <>
            <View style={styles.placedByContainer}>
              <CreditRequestPlacedBy creditRequestBatch={creditRequestBatch} />
            </View>
            <View style={styles.totalRow} testID="total-row">
              <MultiStyleText
                defaultStyle={styles.totalStandard}
                emphasisStyle={styles.totalEmphasis}
                forceSingleLine
              >
                {i18n.t(
                  unitCount === 1
                    ? 'unit_count_emphasis'
                    : 'unit_count_emphasis_plural',
                  { count: unitCount },
                )}
              </MultiStyleText>
              <Text style={styles.totalEmphasis}>{totalPrice}</Text>
            </View>
          </>
        ) : null,
        showBackButton: isSmallLayout,
        title: (
          <Typography variant="heading-sm">
            {creditRequestBatch.customer_facing_id}
          </Typography>
        ),
      };
    }

    return {
      actions,
      contentContainerStyle: navigationHeaderContainerStyle,
      contentFooter: creditRequestBatch ? (
        <>
          <View style={styles.navigationHeaderContentFooterLarge}>
            <CreditRequestPlacedBy creditRequestBatch={creditRequestBatch} />
          </View>
          <CreditRequestDetailPrices
            totalPrice={totalPrice}
            unitCount={unitCount}
          />
        </>
      ) : null,
      contentStyle: styles.navigationHeaderContentLarge,
      title: (
        <Typography testID="credit-request-detail-title" variant="heading-sm">
          {creditRequestBatch.customer_facing_id}
        </Typography>
      ),
    };
  }, [
    creditRequestActions,
    creditRequestBatch,
    creditRequestBatchLineItems,
    currencyCode,
    isSmallLayout,
    loading,
    navigationHeaderContainerStyle,
    styles,
  ]);

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={navigationHeaderProps}
    >
      {(scrollViewProps) => (
        <>
          {!loading && creditRequestBatch ? (
            <View style={styles.listContainer}>
              {creditRequestBatchLineItems?.length ? (
                <CreditRequestDetailList
                  creditRequestBatchLineItems={creditRequestBatchLineItems}
                  scrollViewProps={scrollViewProps}
                />
              ) : null}
            </View>
          ) : null}
          {showMoreActionsSheet ? (
            <CombinedActionSheet
              contentStyle={styles.actionsContentStyle}
              initialRoute={CreditRequestActionSheetRoute.Actions}
              onClose={() => setShowMoreActionsSheet(false)}
              renderScene={({ jumpTo, routeKey }) => {
                switch (routeKey) {
                  case CreditRequestActionSheetRoute.Actions:
                    return (
                      <List<CreditRequestActionListOption>
                        data={creditRequestActions}
                        onSelect={async (item) => {
                          if (
                            item.value ===
                            CreditRequestActionSheetAction.SendToMobilePrinter
                          ) {
                            jumpTo(
                              CreditRequestActionSheetRoute.PrintConfirmation,
                            );
                          } else {
                            await openInvoicePDF();
                            setShowMoreActionsSheet(false);
                          }
                        }}
                        renderLeftComponent={(item) => {
                          const { Icon, iconProps } = item.metadata!;
                          return <Icon height={24} width={24} {...iconProps} />;
                        }}
                      />
                    );
                  case CreditRequestActionSheetRoute.PrintConfirmation:
                    return (
                      <PrintLabelConfirmation
                        buttonText={i18n.t('print_credit_request')}
                        onClose={() => setShowMoreActionsSheet(false)}
                        onPrint={() =>
                          showToast({
                            content: i18n.t('credit_request_sent_to_printer'),
                            type: Type.POSITIVE,
                          })
                        }
                        renderTemplate={async () => {
                          const { data } = await renderCreditRequestTemplate({
                            variables: {
                              creditRequestBatchUUID,
                              restaurantUUID,
                              supplierUUID,
                            },
                          });

                          return data?.renderPrintCreditRequestTemplate
                            .rendered_template;
                        }}
                        style={styles.printConfirmation}
                      />
                    );
                  default:
                    return null;
                }
              }}
              routes={{
                [CreditRequestActionSheetRoute.Actions]: {
                  previousKey: null,
                },
                [CreditRequestActionSheetRoute.PrintConfirmation]: {
                  previousKey: null,
                },
              }}
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  actionsContentStyle: {
    paddingHorizontal: 0,
    paddingTop: 24,
  },
  listContainer: {
    flex: 1,
    paddingTop: 16,
  },
  loadingContainer: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: SCREEN_PADDING,
  },
  navigationHeaderContentFooterLarge: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    paddingBottom: 8,
  },
  navigationHeaderContentLarge: {
    height: 'auto',
  },
  placedByContainer: {
    paddingBottom: 8,
  },
  printConfirmation: {
    marginHorizontal: SCREEN_PADDING_SMALL,
  },
  totalEmphasis: {
    ...FONTS.primarySemiBold,
    fontSize: 22,
    lineHeight: 24,
  },
  totalRow: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 20,
  },
  totalStandard: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 22,
    lineHeight: 24,
  },
}));
